import React, { FC } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { WidgetLayout } from '../../../lib/widget-layout'
import useText from '../../../common/hooks/useText'
import { useCompanyGetCompanyByIdQuery, useCompanyUpdateCompanyMutation } from '../../../common/redux/api/web-api'
import Input from '../../../common/components/Input'
import { Button } from '../../../common/components/Button'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import {
    MAX_NAME_LENGTH,
    MAX_ORG_EMAIL_LENGTH,
    MAX_ORG_DESC_LENGTH,
    MAX_ORG_PHONE_NO_LENGTH,
} from '../../../common/constants/InputsLength'

type FormDataType = { name?: string | null; description?: string | null; email?: string | null; phone?: string | null }

type OrganisationDetailsWidgetProps = { organisationId: string }

export const OrganisationDetailsWidget: FC<OrganisationDetailsWidgetProps> = ({ organisationId }) => {
    const text = useText()
    const { data: organisation } = useCompanyGetCompanyByIdQuery(
        { companyId: organisationId },
        { skip: !organisationId }
    )
    const [updateOrganisation, { isLoading }] = useCompanyUpdateCompanyMutation()

    const schema = yup.object().shape({
        name: yup.string().required(text('msgCompanyNameRequired')),
        email: yup.string().email(text('msgEmailFormatIsIncorrect')),
    })

    const {
        handleSubmit,
        register,
        formState: { errors, isSubmitting, isDirty, isValid },
    } = useForm<FormDataType>({
        resolver: yupResolver(schema),
        mode: 'all',
        values: {
            name: organisation?.name,
            description: organisation?.description,
            email: organisation?.email,
            phone: organisation?.phone,
        },
    })

    const onSubmit = (data: FormDataType) => {
        updateOrganisation({ company: { ...organisation, ...data } })
            .unwrap()
            .then(() => notifySuccess(text('msgUpdateOrganisation')))
            .catch(() => {
                notifyError(text('msgUpdateOrganisationError'))
            })
    }

    return (
        <WidgetLayout
            className="flex w-full"
            title={text('lblCompanyModal', { name: organisation?.name })}
            actionButtons={
                <Button
                    label={text('btnSave')}
                    primary
                    type="submit"
                    form="organisation-details-form"
                    disabled={isSubmitting || !isDirty || !isValid || isLoading}
                />
            }
        >
            <form
                id="organisation-details-form"
                data-testid="organisation-details-form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                autoComplete="off"
            >
                <Input
                    data-testid="company-name-input"
                    wrapperClassName="mt-6"
                    id={'name'}
                    labelText={text('lblName')}
                    {...register('name')}
                    error={errors.name?.message}
                    maxLength={MAX_NAME_LENGTH}
                />
                <Input
                    data-testid="company-description-input"
                    wrapperClassName="mt-6"
                    id={'description'}
                    labelText={text('lblDescription')}
                    {...register('description')}
                    error={errors.description?.message}
                    maxLength={MAX_ORG_DESC_LENGTH}
                />
                <Input
                    data-testid="company-email-input"
                    wrapperClassName="mt-6"
                    id={'email'}
                    labelText={text('lblEmail')}
                    {...register('email')}
                    error={errors.email?.message}
                    maxLength={MAX_ORG_EMAIL_LENGTH}
                />
                <Input
                    data-testid="company-phone-input"
                    wrapperClassName="mt-6"
                    id={'phone'}
                    labelText={text('lblPhone')}
                    {...register('phone')}
                    error={errors.phone?.message}
                    maxLength={MAX_ORG_PHONE_NO_LENGTH}
                />
            </form>
        </WidgetLayout>
    )
}
